import axios from "axios";
import authHeader from "../../services/auth-header";
import globalParams from "../../services/global-params";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const vocabPath = (suffix) => `${BASE_URL}/vocab-mapping/${suffix}`;

export const syncJobVocabApi = async ({ jobId, suggest = false, org }) => {
  const res = await axios.post(
    vocabPath(`jobs/${jobId}/sync`),
    {},
    {
      params: { ...globalParams(), org, suggest: suggest ? "1" : undefined },
      headers: { ...authHeader() },
    }
  );
  return res.data;
};

export const fetchJobVocabProgressApi = async ({ jobId, org }) => {
  const res = await axios.get(vocabPath(`jobs/${jobId}/progress`), {
    params: { ...globalParams(), org },
    headers: { ...authHeader() },
  });
  return res.data;
};

export const fetchJobVocabSegmentsApi = async ({ jobId, status, org }) => {
  const res = await axios.get(vocabPath(`jobs/${jobId}/segments`), {
    params: { ...globalParams(), org, status },
    headers: { ...authHeader() },
  });
  return res.data;
};

export const fetchJobVocabWordsApi = async ({
  jobId,
  shapeId,
  shape_id,
  status,
  page = 1,
  pageSize,
  page_size,
  suggest = false,
  org,
}) => {
  const resolvedShapeId = shapeId ?? shape_id;
  const resolvedPageSize = pageSize ?? page_size ?? 200;
  const res = await axios.get(vocabPath(`jobs/${jobId}/words`), {
    params: {
      ...globalParams(),
      org,
      shape_id: resolvedShapeId,
      status,
      page,
      page_size: resolvedPageSize,
      suggest: suggest ? "1" : undefined,
    },
    headers: { ...authHeader() },
  });
  return res.data;
};

export const searchDictionaryApi = async ({
  query,
  q,
  field = "any",
  limit = 25,
  jobId,
  projectId,
  groupId,
  expandGroups = false,
  org,
}) => {
  const searchQuery = (query ?? q ?? "").trim();
  const res = await axios.get(vocabPath("dictionary/entries/search"), {
    params: {
      ...globalParams(),
      org,
      q: searchQuery,
      field,
      limit,
      job_id: jobId,
      project_id: projectId,
      group_id: groupId,
      expand_groups: expandGroups ? "1" : undefined,
    },
    headers: { ...authHeader() },
  });
  return res.data;
};

export const searchDemoDictionaryApi = async ({
  query,
  q,
  field = "any",
  limit = 25,
}) => {
  const searchQuery = (query ?? q ?? "").trim();
  const res = await axios.get(vocabPath("demo/search"), {
    params: {
      q: searchQuery,
      field,
      limit,
    },
  });
  return res.data;
};

export const fetchDemoDictionaryWordApi = async ({ lex }) => {
  const res = await axios.get(vocabPath("demo/word"), {
    params: { lex },
  });
  return res.data;
};

export const addWordMappingsApi = async ({ wordId, entryIds, confidence, notes, org }) => {
  const res = await axios.post(
    vocabPath(`words/${wordId}/mappings`),
    { entry_ids: entryIds, confidence, notes },
    {
      params: { ...globalParams(), org },
      headers: { ...authHeader() },
    }
  );
  return res.data;
};

export const removeWordMappingApi = async ({ wordId, entryId, org }) => {
  await axios.delete(vocabPath(`words/${wordId}/mappings/${entryId}`), {
    params: { ...globalParams(), org },
    headers: { ...authHeader() },
  });
};

export const updateSegmentPhoneticsApi = async ({ segmentId, tokens, org }) => {
  const res = await axios.patch(
    vocabPath(`segments/${segmentId}/phonetics`),
    { tokens },
    {
      params: { ...globalParams(), org },
      headers: { ...authHeader() },
    }
  );
  return res.data;
};

export const addTempEntryApi = async ({ wordId, lex, phono, englishGloss, org }) => {
  const res = await axios.post(
    vocabPath(`words/${wordId}/temp-entry`),
    { lex, phono, english_gloss: englishGloss },
    {
      params: { ...globalParams(), org },
      headers: { ...authHeader() },
    }
  );
  return res.data;
};

export const fetchProjectVocabProgressApi = async ({ projectId, org }) => {
  const res = await axios.get(vocabPath(`projects/${projectId}/progress`), {
    params: { ...globalParams(), org },
    headers: { ...authHeader() },
  });
  return res.data;
};

export const exportProjectVocabApi = async ({ projectId, org }) => {
  const res = await axios.get(vocabPath(`projects/${projectId}/export`), {
    params: { ...globalParams(), org },
    headers: { ...authHeader() },
    responseType: "blob",
  });
  return res.data;
};

export const syncJobWordsApi = syncJobVocabApi;
export const fetchJobProgressApi = fetchJobVocabProgressApi;
export const fetchJobSegmentsApi = fetchJobVocabSegmentsApi;
export const fetchJobWordsApi = fetchJobVocabWordsApi;
export const fetchProjectProgressApi = fetchProjectVocabProgressApi;
export const exportProjectMappingsApi = exportProjectVocabApi;

export const fetchDictionaryVersionsApi = async ({ org } = {}) => {
  const res = await axios.get(vocabPath("dictionary/versions"), {
    params: { ...globalParams(), org },
    headers: { ...authHeader() },
  });
  return res.data;
};

export const importDictionaryApi = async ({ file, version, activate = true, org }) => {
  const formData = new FormData();
  formData.append("file", file);
  if (version) formData.append("version", version);
  formData.append("activate", activate ? "true" : "false");

  const res = await axios.post(vocabPath("dictionary/versions/import"), formData, {
    params: { ...globalParams(), org },
    headers: { ...authHeader() },
  });
  return res.data;
};
